import type { TaskType, TaskField, TaskFieldValue, Task } from "./types";

/* Task completion gate — client mirror of api/src/tasks/completion.ts. Keeps the
 * UI's idea of "done" byte-identical to the server's so the Complete button and
 * the API agree on exactly which fields are still required. */

export const DEFAULT_TASK_TYPES: TaskType[] = [
  { id: "tt_onboard", name: "Onboarding kickoff", description: "Stand up a new customer. Can't close until the plan is in place.", icon: "target", system: true, fields: [
    { key: "kickoff_held", label: "Kickoff call held", kind: "checkbox", required: true },
    { key: "goals", label: "Success goals agreed", kind: "text", required: true, help: "What does the customer want to achieve in 90 days?" },
    { key: "go_live", label: "Target go-live date", kind: "date", required: true },
    { key: "exec_sponsor", label: "Executive sponsor", kind: "text", required: false },
  ] },
  { id: "tt_qbr", name: "Renewal QBR", description: "Quarterly business review ahead of a renewal.", icon: "doc", system: true, fields: [
    { key: "deck_sent", label: "QBR deck shared", kind: "checkbox", required: true },
    { key: "outcome", label: "Renewal sentiment", kind: "select", required: true, options: ["On track", "At risk", "Needs escalation"] },
    { key: "next_step", label: "Agreed next step", kind: "text", required: true },
  ] },
  { id: "tt_escalation", name: "Escalation", description: "Resolve a customer escalation. Requires a documented resolution.", icon: "bolt", system: true, fields: [
    { key: "root_cause", label: "Root cause", kind: "text", required: true },
    { key: "resolution", label: "Resolution summary", kind: "text", required: true },
    { key: "customer_confirmed", label: "Customer confirmed resolved", kind: "checkbox", required: true },
  ] },
  { id: "tt_checkin", name: "Check-in", description: "A lightweight touch-base with an optional note.", icon: "check", fields: [
    { key: "notes", label: "Call notes", kind: "text", required: false },
  ] },
];

export function fieldSatisfied(field: TaskField, value: TaskFieldValue | undefined): boolean {
  if (value === undefined || value === null) return false;
  switch (field.kind) {
    case "checkbox": return value === true;
    case "number": return typeof value === "number" && !Number.isNaN(value);
    case "select": return typeof value === "string" && (field.options ?? []).includes(value);
    default: return String(value).trim().length > 0;
  }
}

export function missingRequiredFields(type: TaskType | undefined, fields: Record<string, TaskFieldValue> | undefined): TaskField[] {
  if (!type) return [];
  const f = fields ?? {};
  return type.fields.filter((field) => field.required && !fieldSatisfied(field, f[field.key]));
}

export function canComplete(type: TaskType | undefined, fields: Record<string, TaskFieldValue> | undefined): boolean {
  return missingRequiredFields(type, fields).length === 0;
}

/** Stamp completion metadata onto an update patch — client mirror of the
 *  server's applyCompletion. When a task crosses into "done", annotate who
 *  completed it (the task keeps its original owner/assignee). */
export function applyCompletion(existing: Task, patch: Partial<Task>, actorId: string | undefined, nowIso: string): Partial<Task> {
  const becomingDone = patch.status === "done" && existing.status !== "done";
  const leavingDone = patch.status !== undefined && patch.status !== "done" && existing.status === "done";
  if (becomingDone && actorId) {
    return { ...patch, completedBy: actorId, completedAt: nowIso };
  }
  if (leavingDone) {
    return { ...patch, completedBy: undefined, completedAt: undefined };
  }
  return patch;
}
